import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Gift } from 'lucide-react';
// Small, heavily-optimized Unsplash thumbnails: auto=format (webp/avif), low quality,
// tiny width, cropped square. Rendered ~56px so w=160 covers retina.
const thumb = (id: string) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=160&h=160&q=55`;
const tomos = [{
  num: 1,
  img: thumb('1512069772995-ec65ed45afd6'),
  alt: 'Cronograma de tratamiento dermatológico',
  title: 'Protocolo de Adherencia Terapéutica',
  desc: 'Cronogramas clínicos por patología: qué esperar en la semana 1, 4, 8 y 12.'
}, {
  num: 2,
  img: thumb('1629909613654-28e377c37b09'),
  alt: 'Procedimiento estético con láser',
  title: 'Protocolo de Alarma Post-Procedimiento',
  desc: "Tarjetas de 'normal vs. urgencia' para peeling, láser, crioterapia y biopsias."
}, {
  num: 3,
  img: thumb('1587560699334-cc4ff634909a'),
  alt: 'Comunicación con pacientes por mensajería',
  title: 'Protocolo de Comunicación Estandarizada',
  desc: 'Banco de 20 respuestas clínicas listas para copiar y pegar.'
}, {
  num: 4,
  img: thumb('1450101499163-c8848c66ca85'),
  alt: 'Firma de consentimiento informado',
  title: 'Protocolo de Consentimiento y Resguardo Legal',
  desc: 'Modelos editables por procedimiento. Firmado antes, resguardado siempre.'
}, {
  num: 5,
  img: thumb('1579684385127-1ef15d508118'),
  alt: 'Primera consulta dermatológica',
  title: 'Protocolo de Primera Consulta y Anamnesis',
  desc: 'Fichas de piel con escalas de severidad. Anamnesis en menos de 3 minutos.'
}, {
  num: 6,
  img: thumb('1576091160550-2173dba999ef'),
  alt: 'Educación del paciente en consultorio',
  title: 'Protocolo de Educación del Paciente',
  desc: 'Cuadernillos de cuidado personalizables con tu marca, por patología.'
}, {
  num: 7,
  img: thumb('1554224155-6726b3ff858f'),
  alt: 'Presupuesto de procedimientos estéticos',
  title: 'Protocolo Comercial de Procedimientos Estéticos',
  desc: 'Guion de presentación y plantilla de presupuesto transparente.'
}, {
  num: 8,
  img: thumb('1611162617474-5b21e879e113'),
  alt: 'Marca profesional en redes sociales',
  title: 'Protocolo de Marca Profesional',
  desc: 'Plantillas de contenido para redes que reflejan tu matrícula.'
}];
const bonos = [{
  img: thumb('1506784983877-45594efa4cbe'),
  alt: 'Calendario de controles y recordatorios',
  title: 'Calendario de Controles y Recordatorios',
  desc: 'Textos pre-armados para recordar turnos según tipo de tratamiento.'
}, {
  img: thumb('1484480974693-6ca0a78fb36b'),
  alt: 'Checklist de cierre de jornada',
  title: 'Protocolo de Cierre de Jornada',
  desc: 'Checklist de 5 minutos para cerrar el consultorio sin pendientes.'
}, {
  img: thumb('1454165804606-c3d57bc86b40'),
  alt: 'Guía de derivación de pacientes',
  title: 'Guía de Derivación Inteligente',
  desc: 'Frases para derivar al paciente a tu propio material.'
}];
export function Contents() {
  return <section className="py-12 md:py-24 bg-brand-navy text-white relative overflow-hidden" data-id="element-37">
      <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-38"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-39">
        <div className="text-center max-w-3xl mx-auto mb-6 md:mb-12" data-id="element-40">
          <h2 className="text-xs md:text-sm font-bold text-brand-gold uppercase tracking-widest mb-2 md:mb-3" data-id="element-41">
            Contenido Completo
          </h2>
          <h3 className="text-2xl md:text-4xl font-serif font-bold mb-2 md:mb-4" data-id="element-42">
            Los 8 Tomos
          </h3>
          <p className="text-sm md:text-lg text-slate-300" data-id="element-43">
            Tu sistema de protocolos para cada momento del consultorio. 110
            páginas de contenido práctico.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-8 md:mb-16" data-id="element-44">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} whileHover={{
          x: 4
        }} transition={{
          duration: 0.3,
          delay: index * 0.04
        }} className="bg-brand-blue/50 border border-brand-blue p-3 rounded-xl flex items-center gap-3 hover:bg-brand-blue transition-all cursor-default group" data-id="element-45">
              <img src={tomo.img} alt={tomo.alt} loading="lazy" decoding="async" width={56} height={56} className="flex-shrink-0 w-14 h-14 rounded-lg object-cover ring-1 ring-brand-gold/30 group-hover:ring-brand-gold/70 transition-all" data-id="element-46" />
              <div className="min-w-0" data-id="element-47">
                <div className="text-[10px] font-bold text-brand-gold uppercase tracking-wider" data-id="element-48">
                  Tomo {tomo.num}
                </div>
                <h4 className="text-sm md:text-base font-bold leading-tight mb-0.5" data-id="element-49">
                  {tomo.title}
                </h4>
                <p className="text-slate-300 text-xs leading-snug line-clamp-2" data-id="element-50">
                  {tomo.desc}
                </p>
              </div>
            </motion.div>)}
        </div>

        <div className="bg-gradient-to-br from-brand-gold/20 to-transparent border border-brand-gold/30 rounded-2xl md:rounded-3xl p-5 md:p-10" data-id="element-51">
          <div className="text-center mb-5 md:mb-8" data-id="element-52">
            <div className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 bg-brand-gold rounded-full text-brand-navy mb-3 shadow-[0_0_15px_rgba(201,162,39,0.5)]" data-id="element-53">
              <Gift className="w-6 h-6 md:w-7 md:h-7" data-id="element-54" />
            </div>
            <h3 className="text-xl md:text-3xl font-serif font-bold text-brand-gold mb-1" data-id="element-55">
              3 Bonos de Regalo
            </h3>
            <p className="text-sm md:text-base text-slate-300" data-id="element-56">
              Sin costo extra — incluidos en ambas ofertas
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4" data-id="element-57">
            {bonos.map((bono, index) => <motion.div key={index} whileHover={{
            y: -4
          }} className="bg-brand-navy/50 p-3 rounded-xl border border-brand-gold/20 hover:border-brand-gold/50 transition-colors flex items-center gap-3" data-id="element-58">
                <img src={bono.img} alt={bono.alt} loading="lazy" decoding="async" width={48} height={48} className="flex-shrink-0 w-12 h-12 rounded-lg object-cover ring-1 ring-brand-gold/30" data-id="element-59" />
                <div className="min-w-0" data-id="element-60">
                  <div className="text-brand-gold font-bold text-[10px] uppercase tracking-wider" data-id="element-61">
                    Bono {index + 1}
                  </div>
                  <h4 className="text-sm font-bold leading-tight mb-0.5" data-id="element-62">
                    {bono.title}
                  </h4>
                  <p className="text-slate-300 text-xs leading-snug line-clamp-2" data-id="element-63">
                    {bono.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}